<script>
  import { AppController } from '../AppController.js';
  import SectionHeader from '../components/SectionHeader.svelte';
  import { toast, stickyToast } from '../components/Toast.svelte';
  import { 
    streets,
    cities,
    states,
    routes,
    propertyTypes,
    divisions,
    wells
  } from '../store.js';
  import { AppFactory } from '../util.js';
  import { showLoading } from '../components/Overlay.svelte';

  import { fade } from 'svelte/transition';
  import { form } from '../validate.js';
  import { push } from 'svelte-spa-router';
  import { to_number } from 'svelte/internal';
 
  // data
  export let params = {};

  let resident = params.resident_id ? 
    { ...AppController.getResident(params.resident_id)  } :
    AppFactory.getNewResident();

  const { validate, ...residentForm} = form(() => ({
    // account: { value: resident.ubmax_acc, validators: [ (acc) => acc ] },
    first_name: { value: resident.first_name, validators: ["required", "min:1"]},
    last_name: { value: resident.last_name, validators: ["required", "min:1"]},
    house_number: { value: resident.house_number, validators: ["required", "min:1"]},
    street: { value: resident.street, validators: ["required", "min:1"]},
    city: { value: resident.city, validators: ["required", "min:1"]},
    state: { value: resident.state, validators: ["required", "min:2"]},
    postal_code: { value: resident.postal_code, validators: ["required", "postal"]},
    email_1: { value: resident.email_1, validators: ["email"]},
    email_2: { value: resident.email_2, validators: ["email"]},
    cell_1: { value: resident.cell_1, validators: ["phone"]},
    cell_2: { value: resident.cell_2, validators: ["phone"]},
    home_phone: { value: resident.home_phone, validators: ["phone"]},
  }));

  //computed
  $: subsection = params.resident_id ? 'Edit Resident' : 'Create Resident';
  $: isNew = !params.resident_id;

  // methods
  const setStreet = (street) => resident.street = street;
  const setCity = (city) => resident.city = city;
  const setState = (state) => resident.state = state;
  const setRoute = (route) => resident.route = route;
  const setPropertyType = (propertyType) => resident.property_type = propertyType;
  const setDivision = (division) => resident.division = division;
  const setWell = (well) => resident.well = well;
  const save = async () => {
    try {
      showLoading(true);
      if(AppController.ubmaxAccExists(resident.ubmax_acc, resident.resident_id)) {
        stickyToast('danger', 'Error', 'UBmax account number already exists. Resident was not saved');
      } else {
        AppController.saveResident(resident);
        toast('success', 3000, 'Saved', 'Resident was saved successfully');
      }
    } catch (err) {
      console.log(err);
      stickyToast('danger', 'Error', 'An error occured saving resident.');
    } finally {
      showLoading(false);
    }
  }

  const cancel = () => {
    push('/app/residents');
  }
</script>
<div in:fade="{{duration: 1000}}">
  <SectionHeader section='Residents' subsection={subsection}>
  </SectionHeader>
  {#if resident && residentForm}
    <div class='row'>
      <div class="col-md-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">Resident Info</h4>
            <form class="forms-sample">
              <div class='row'>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-first-name" class='required-field'>First Name</label>
                    <input 
                      type="text" 
                      bind:value={resident.first_name} 
                      use:validate={resident.first_name}
                      class="form-control" 
                      class:border-danger={(!$residentForm.first_name.valid && !isNew) || 
                        (!$residentForm.first_name.valid && isNew && $residentForm.first_name.dirty)}
                      id="input-first-name" 
                      placeholder="First Name">
                    {#if ($residentForm.first_name.errors.includes('required') && !isNew) || 
                        ($residentForm.first_name.errors.includes('required') && isNew && $residentForm.first_name.dirty)}
                      <p class='text-danger text-small'>First name is required</p>
                    {/if}
                  </div>
                </div>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-last-name" class='required-field'>Last Name</label>
                    <input 
                      type="text" 
                      bind:value={resident.last_name} 
                      use:validate={resident.last_name}
                      class="form-control" 
                      class:border-danger={(!$residentForm.last_name.valid && !isNew) || 
                        (!$residentForm.last_name.valid && isNew && $residentForm.last_name.dirty)}
                      id="input-last-name" 
                      placeholder="Last Name">
                    {#if ($residentForm.last_name.errors.includes('required') && !isNew) ||
                        ($residentForm.last_name.errors.includes('required') && isNew && $residentForm.last_name.dirty)}
                      <p class='text-danger text-small'>Last name is required</p>
                    {/if}
                  </div>
                </div>
              </div>
              <div class='row'>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-house-number" class='required-field'>House Number</label>
                    <input 
                      type="text" 
                      bind:value={resident.house_number} 
                      use:validate={resident.house_number}
                      class="form-control"
                      class:border-danger={(!$residentForm.house_number.valid && !isNew) ||
                        (!$residentForm.house_number.valid && isNew && $residentForm.house_number.dirty)} 
                      id="input-house-number" 
                      placeholder="House Number">
                    {#if ($residentForm.house_number.errors.includes('required') && !isNew) || 
                      ($residentForm.house_number.errors.includes('required') && isNew && $residentForm.house_number.dirty)}
                      <p class='text-danger text-small'>House number is required</p>
                    {/if}
                  </div>
                </div>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-street" class='required-field'>Street</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <button class="btn btn-sm btn-outline dropdown-toggle border-light" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">Select</button>
                        <div class="dropdown-menu">
                          {#each $streets as street}
                            <a class="dropdown-item" on:click|preventDefault={() => setStreet(street)}>{street}</a>
                          {/each}
                        </div>
                      </div>
                      <input 
                        type="text" 
                        bind:value={resident.street} 
                        use:validate={resident.street}
                        class="form-control" 
                        class:border-danger={(!$residentForm.street.valid && !isNew) || 
                          (!$residentForm.street.valid && isNew && $residentForm.street.dirty)} 
                        id="input-street"
                        placeholder='Or enter new street'>
                    </div>
                    {#if ($residentForm.street.errors.includes('required') && !isNew) || 
                      ($residentForm.street.errors.includes('required') && isNew && $residentForm.street.dirty)}
                      <p class='text-danger text-small'>Street is required</p>
                    {/if}
                  </div>
                </div>
              </div>
              <div class='row'>
                <div class='col-md-4'>
                  <div class="form-group">
                    <label for="input-city" class='required-field'>City</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <button class="btn btn-sm btn-outline dropdown-toggle border-light" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">Select</button>
                        <div class="dropdown-menu">
                          {#each $cities as city}
                            <a class="dropdown-item" on:click|preventDefault={() => setCity(city)}>{city}</a>
                          {/each}
                        </div>
                      </div>
                      <input 
                        type="text" 
                        bind:value={resident.city}
                        use:validate={resident.city}
                        class="form-control"
                        class:border-danger={(!$residentForm.city.valid && !isNew) || 
                          (!$residentForm.city.valid && isNew && $residentForm.city.dirty)} 
                        id="input-street"
                        placeholder='Or enter new city'>
                    </div>
                    {#if ($residentForm.city.errors.includes('required') && !isNew) ||
                      ($residentForm.city.errors.includes('required') && isNew && $residentForm.city.dirty)}
                      <p class='text-danger text-small'>City is required</p>
                    {/if}
                  </div>
                </div>
                <div class='col-md-4'>
                  <div class="form-group">
                    <label for="input-state" class='required-field'>State</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <button class="btn btn-sm btn-outline dropdown-toggle border-light" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">Select</button>
                        <div class="dropdown-menu">
                          {#each $states as state}
                            <a class="dropdown-item" on:click|preventDefault={() => setState(state)}>{state}</a>
                          {/each}
                        </div>
                      </div>
                      <input 
                        type="text" 
                        bind:value={resident.state} 
                        use:validate={resident.state}
                        class="form-control" 
                        class:border-danger={(!$residentForm.state.valid && !isNew) ||
                          (!$residentForm.state.valid && isNew && $residentForm.state.dirty)} 
                        id="input-state"
                        placeholder='Or enter new state'>
                    </div>
                    {#if ($residentForm.state.errors.includes('required') && !isNew) || 
                      ($residentForm.state.errors.includes('required') && isNew && $residentForm.state.dirty)}
                      <p class='text-danger text-small'>State is required</p>
                    {/if}
                  </div>
                </div>
                <div class='col-md-4'>
                  <div class="form-group">
                    <label for="input-postal" class='required-field'>Postal Code</label>
                    <input 
                      type="text" 
                      bind:value={resident.postal_code} 
                      use:validate={resident.postal_code}
                      class="form-control" 
                      class:border-danger={(!$residentForm.postal_code.valid && !isNew) || 
                        (!$residentForm.postal_code.valid && isNew && $residentForm.postal_code.dirty)}
                      id="input-postal"
                      placeholder='Postal Code'>
                    {#if (($residentForm.postal_code.errors.includes('required') || $residentForm.postal_code.errors.includes('postal')) && !isNew) ||
                          (($residentForm.postal_code.errors.includes('required') || $residentForm.postal_code.errors.includes('postal')) && isNew && $residentForm.postal_code.dirty)}
                      <p class='text-danger text-small'>Valid postal code is required (eg 55449 or 55449-9090)</p>
                    {/if}
                  </div>

                </div>
              </div>
              <div class='row'>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-postal">Route</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <button class="btn btn-sm btn-outline dropdown-toggle border-light" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">Select</button>
                        <div class="dropdown-menu">
                          {#each $routes as route}
                            <a class="dropdown-item" on:click|preventDefault={() => setRoute(route)}>{route}</a>
                          {/each}
                        </div>
                      </div>
                      <input 
                        type="text" 
                        bind:value={resident.route} 
                        class="form-control" 
                        id="input-postal"
                        placeholder='Or enter new route'>
                    </div>
                  </div>
                </div>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-route">Property Type</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <button class="btn btn-sm btn-outline dropdown-toggle border-light" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">Select</button>
                        <div class="dropdown-menu">
                          {#each $propertyTypes as propertyType}
                            <a class="dropdown-item" on:click|preventDefault={() => setPropertyType(propertyType)}>{propertyType}</a>
                          {/each}
                        </div>
                      </div>
                      <input
                        type="text" 
                        bind:value={resident.property_type} 
                        class="form-control" 
                        id="input-route"
                        placeholder='Or enter new property type'>
                    </div>
                  </div>
                </div>
              </div>

              <div class='row'>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-division">Division</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <button class="btn btn-sm btn-outline dropdown-toggle border-light" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">Select</button>
                        <div class="dropdown-menu">
                          {#each $divisions as division}
                            <a class="dropdown-item" on:click|preventDefault={() => setDivision(division)}>{division}</a>
                          {/each}
                        </div>
                      </div>
                      <input 
                        type="text" 
                        bind:value={resident.division} 
                        class="form-control" 
                        id="input-division"
                        placeholder='Or enter new division'>
                    </div>
                  </div>
                </div>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-well">Well</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <button class="btn btn-sm btn-outline dropdown-toggle border-light" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">Select</button>
                        <div class="dropdown-menu">
                          {#each $wells as well}
                            <a class="dropdown-item" on:click|preventDefault={() => setWell(well)}>{well}</a>
                          {/each}
                        </div>
                      </div>
                      <input
                        type="text" 
                        bind:value={resident.well} 
                        class="form-control" 
                        id="input-route"
                        placeholder='Or enter new well'>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class='row'>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-email-1">Primary Email</label>
                    <input 
                      type="text" 
                      bind:value={resident.email_1} 
                      class="form-control" 
                      class:border-danger={$residentForm.email_1.errors.length}
                      id="input-email-1" 
                      placeholder="Primary Email">
                    {#if $residentForm.email_1.errors.includes('required') || $residentForm.email_1.errors.includes("email")}
                      <p class='text-danger text-small'>Valid email address is required</p>
                    {/if}
                  </div>
                </div>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-email-2">Secondary Email</label>
                    <input 
                      type="text" 
                      bind:value={resident.email_2} 
                      class="form-control" 
                      class:border-danger={$residentForm.email_2.errors.length}
                      id="input-email-2" 
                      placeholder="Secondary Email">
                    {#if $residentForm.email_2.errors.includes('email')}
                      <p class='text-danger text-small'>Must be a valid email address or empty</p>
                    {/if}
                  </div>
                </div>
              </div>
              <div class='row'>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-cell-1">Primary Cell Phone</label>
                    <input 
                      type="text" 
                      bind:value={resident.cell_1} 
                      class="form-control" 
                      class:border-danger={$residentForm.cell_1.errors.length}
                      id="input-cell-1" 
                      placeholder="Primary Cell Phone">
                    {#if $residentForm.cell_1.errors.includes('phone')}
                      <p class='text-danger text-small'>Must be valid phone number or empty (eg 612-633-9090 or (612) 633-9090)</p>
                    {/if}
                  </div>
                </div>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-cell-2">Secondary Cell Phone</label>
                    <input 
                      type="text" 
                      bind:value={resident.cell_2} 
                      class="form-control" 
                      class:border-danger={$residentForm.cell_2.errors.length}
                      id="input-cell-2" 
                      placeholder="Secondary Cell Phone">
                    {#if $residentForm.cell_2.errors.includes('phone')}
                      <p class='text-danger text-small'>Must be valid phone number or empty (eg 612-633-9090 or (612) 633-9090)</p>
                    {/if}
                  </div>
                </div>
              </div>
              <div class='row'>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-cell-1">Home Phone</label>
                    <input 
                      type="text" 
                      bind:value={resident.home_phone} 
                      class="form-control" 
                      class:border-danger={$residentForm.home_phone.errors.length}
                      id="input-home-phone" 
                      placeholder="Home Phone">
                    {#if $residentForm.home_phone.errors.includes('phone')}
                      <p class='text-danger text-small'>Must be valid phone number or empty (eg 612-633-9090 or (612) 633-9090)</p>
                    {/if}
                  </div>
                </div>
                <div class='col-md-6'>
                  <div class="form-group">
                    <label for="input-cell-1">UBMax Acc</label>
                    <input 
                      type="text" 
                      bind:value={resident.ubmax_acc} 
                      class="form-control" 
                      id="input-ubmax-acc" 
                      placeholder="UBMax Account Number">
                  </div>
                </div>
              </div>
              <button class="btn btn-secondary mr-2" on:click|preventDefault={cancel}>Cancel</button>
              <button class="btn btn-primary mr-2" class:disabled={!$residentForm.valid} disabled={!$residentForm.valid}  on:click|preventDefault={save}>Save</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .required-field::after {
    content: "*";
    color: red;
  }

  .border-light {
    border: 1px solid #f3f3f3;
  }

  .dropdown-menu {
    position: absolute;
    will-change: transform;
    overflow-anchor: auto;
    height: 200px;
    overflow-y: auto;
    top: 0px;
    left: 0px;
    transform: translate3d(0px, 46px, 0px);
  }
</style>
